import { Component, inject, signal } from '@angular/core';
import { PermissionTypeEnum, UserRoleType } from '@core/enums/enums';
import { PermissionService } from '@core/services/permission.service';
import { AppService } from '@core/services/app.service';
import { AuthService } from '@core/services/auth.services';
import { PROVIDER_BOOKS_ROLES } from '@core/utils/provider-books.utils';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { lastValueFrom } from 'rxjs';
import { LoadsService } from '@pages/loads/loads.service';
import { LoadQueryFilterRequest } from '@pages/loads/loads.types';

export interface MenuItemDto {
  key?: string;
  label: string;
  icon?: string;
  tooltip?: string;
  routerLink?: string[];
  items?: MenuItemDto[];
  order?: number;
  loading?: boolean;
  queryParams?: any;
  permission?: PermissionTypeEnum | PermissionTypeEnum[] | null;
  /** Role-based gate for menu items that don't have a backing permission yet. */
  roles?: UserRoleType[];
  /**
   * Also require the tenant's assisted-contract-creation switch. Role alone is not the gate for these
   * screens - the backend pairs [Authorize(GLOBAL_ADMIN_ONLY)] with [RequireContractWizard] - so an
   * entry offered on role alone leads a Global Admin to a 404 in a tenant that has the feature off.
   */
  requiresContractWizard?: boolean;
  /**
   * The wide desktop menu (Dan, 2026-10-16): a group row only opens and closes its list, so a group that is also
   * a page lists that page first under this label. Absent where the group's own page is already a child
   * (Finance lands on Vouchers).
   */
  overviewLabel?: string;
}

const MENU_COUNTS_FRESH_MS = 15_000;

@Component({
  selector: 'app-menu',
  standalone: false,
  templateUrl: './app.menu.component.html'
})
export class AppMenuComponent {
  private permissionService = inject(PermissionService);
  private appService = inject(AppService);
  private loadService = inject(LoadsService);
  private authService = inject(AuthService);

  private readonly searchLoadsKey = 'search-loads';
  private menuBase: MenuItemDto[] = [
    {
      label: 'Dashboard',
      icon: 'pi pi-home',
      tooltip: 'Dashboard',
      routerLink: ['/home'],
      permission: PermissionTypeEnum.CanHomeViewPage,
      order: 1
    },
    {
      label: 'New Quote',
      icon: 'pi pi-fw pi-calculator',
      routerLink: ['/quotes/rate'],
      order: 2,
      permission: PermissionTypeEnum.CanLoadCreatePage,
      tooltip: 'New Quote'
    },
    {
      key: this.searchLoadsKey,
      label: 'Search Loads',
      icon: 'pi pi-fw pi-search',
      routerLink: ['/loads'],
      queryParams: { searchMode: 'default' },
      overviewLabel: 'All loads',
      permission: PermissionTypeEnum.CanLoadListPage,
      items: [
        { label: 'In Transit', routerLink: ['loads'], queryParams: { searchMode: 'intransit' } },
        { label: 'Dispatched', routerLink: ['loads'], queryParams: { searchMode: 'dispatched' } },
        { label: 'Booked', routerLink: ['loads'], queryParams: { searchMode: 'booked' } },
        { label: 'Awarded Not Covered', routerLink: ['loads'], queryParams: { searchMode: 'awarded-not-covered' } },
        { label: 'Audit Exceptions', routerLink: ['loads'], queryParams: { searchMode: 'audit-exceptions' } },
        { label: 'Service Exceptions', routerLink: ['loads'], queryParams: { searchMode: 'service-exceptions' } },
        // Integration issues are the provider's intake queue (RULINGS #483): the roles that run the provider's books only.
        {
          label: 'Integration Exceptions',
          routerLink: ['loads'],
          queryParams: { searchMode: 'integration-exceptions' },
          roles: [...PROVIDER_BOOKS_ROLES]
        }
      ],
      order: 3
    },
    {
      label: 'Organizations',
      // pi-fw to match the fixed-width sizing of the other top-level items;
      // this was the only Font Awesome icon in the menu.
      icon: 'pi pi-fw pi-building',
      routerLink: ['/organizations'],
      overviewLabel: 'All organizations',
      items: [{ label: 'New Company', icon: 'pi pi-plus', routerLink: ['/organizations/newcompany'], permission: PermissionTypeEnum.CanCompanyCreatePage }],
      order: 4,
      permission: [PermissionTypeEnum.CanCompanyListPage, PermissionTypeEnum.CanLogisticsServiceProviderListPage, PermissionTypeEnum.CanMarketPartnerListPage]
    },
    {
      label: 'Carriers',
      icon: 'pi pi-fw pi-truck',
      routerLink: ['/carriers/listcarriers'],
      overviewLabel: 'All carriers',
      items: [{ label: 'New Carrier', icon: 'pi pi-plus', routerLink: ['carriers/newcarrier'], permission: PermissionTypeEnum.CanCarrierCreatePage }],
      order: 5,
      permission: PermissionTypeEnum.CanCarrierListPage
    },
    {
      label: 'Contracts',
      icon: 'pi pi-fw pi-file-check',
      routerLink: ['/contracts/listcontracts'],
      overviewLabel: 'All contracts',
      items: [{ label: 'New Contract', icon: 'pi pi-plus', routerLink: ['contracts/newcontract'], permission: PermissionTypeEnum.CanContractCreatePage }],
      order: 6,
      permission: PermissionTypeEnum.CanContractListPage
    },
    {
      label: 'Finance',
      icon: 'pi pi-money-bill',
      // Every other root item routes somewhere; without this Finance was the only one that did
      // not, which the labelled rail made look like a broken link. It also left the anchor with
      // no `href`, so it was not focusable and a keyboard user could not open the fly-out at all —
      // making Vendors and Vouchers unreachable from the menu. `financeRoutes` redirects '' to
      // 'vouchers', so this lands on Vouchers; the fly-out still opens on hover as before.
      routerLink: ['/finance'],
      items: [
        { label: 'Vendors', icon: 'pi pi-briefcase', routerLink: ['/finance/vendors'], permission: PermissionTypeEnum.CanFinanceViewPage },
        {
          label: 'Vouchers',
          icon: 'pi pi-receipt',
          routerLink: ['/finance/vouchers'],
          permission: PermissionTypeEnum.CanFinanceViewPage
        }
      ],
      order: 7,
      permission: PermissionTypeEnum.CanFinanceViewPage
    },
    {
      label: 'Manage Users',
      icon: 'pi pi-fw pi-users',
      routerLink: ['/users'],
      overviewLabel: 'All users',
      items: [{ label: 'New User', icon: 'pi pi-plus', routerLink: ['/users/new'], roles: ['GlobalAdmin', 'TenantAdmin', 'Admin', 'ProviderAdmin'] }],
      order: 8,
      // TODO: switch to `permission: PermissionTypeEnum.CanUserManageListPage` once IAM seeds it (see approved plan)
      roles: ['GlobalAdmin', 'TenantAdmin', 'Admin', 'ProviderAdmin']
    },
    {
      label: 'Admin',
      icon: 'pi pi-fw pi-shield',
      tooltip: 'Admin',
      order: 9,
      // ProviderAdmin included so LSPs reach E-Signature Requests (its only child, which carries its
      // own role gate; the backend scopes their rows to their own customers). The child route gate
      // allowed them already — the parent group was what hid it.
      roles: ['GlobalAdmin', 'TenantAdmin', 'ProviderAdmin'],
      items: [
        {
          label: 'E-Signature Requests',
          icon: 'pi pi-verified',
          routerLink: ['/esign-requests'],
          roles: ['GlobalAdmin', 'TenantAdmin', 'ProviderAdmin']
        },
        {
          // The domain tier of the Theme screen (US 3522) — the tier every provider and company falls back to.
          label: 'Theme',
          icon: 'pi pi-palette',
          routerLink: ['/tenant-theme'],
          roles: ['GlobalAdmin']
        },
        {
          label: 'Carrier API Health',
          icon: 'pi pi-fw pi-wave-pulse',
          routerLink: ['/carrier-api-health'],
          requiresContractWizard: true
        },
        {
          // The blocks belong to the LSP, so a Provider Admin manages their own without a platform admin.
          label: 'PRO Numbers',
          icon: 'pi pi-fw pi-hashtag',
          routerLink: ['/pro-numbers'],
          roles: ['GlobalAdmin', 'TenantAdmin', 'ProviderAdmin']
        }
      ]
    }
  ];

  public menuItems = signal<MenuItemDto[]>([]);
  private currentActiveItem = signal<MenuItemDto | null>(null);

  constructor() {
    this.permissionService
      .getPermissions()
      .pipe(takeUntilDestroyed())
      .subscribe((permissions) => {
        const filteredMenu = this.filterMenuByPermissions(this.menuBase, permissions);
        this.menuItems.set(filteredMenu.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
      });
  }

  /**
   * Filters menu items recursively based on user permissions.
   * A parent menu item is only included if:
   * - It has no permission requirement (permission === null)
   * - The user has the required permission
   * - After filtering, it has at least one visible child item (if it has children)
   */
  private filterMenuByPermissions(menuItems: MenuItemDto[], permissions: number[]): MenuItemDto[] {
    return menuItems
      .map((item) => {
        // Clone the item to avoid mutating the original
        const clonedItem = { ...item };

        // If the item has children, filter them recursively
        if (clonedItem.items && clonedItem.items.length > 0) {
          const filteredChildren = this.filterMenuByPermissions(clonedItem.items, permissions);
          clonedItem.items = filteredChildren;
        }

        return clonedItem;
      })
      .filter((item) => {
        // Check if the item itself has permission to be displayed (an array is "any one of these")
        const hasPermission =
          item.permission == null ||
          (Array.isArray(item.permission) ? item.permission.some((p) => permissions.includes(Number(p))) : permissions.includes(Number(item.permission)));
        const hasRole = !item.roles || item.roles.length === 0 || this.authService.hasAnyRole(item.roles);
        const hasFeature = !item.requiresContractWizard || this.appService.isFeatureEnabled()();

        // If the item has no permission, or fails its role or tenant-feature gate, don't show it
        if (!hasPermission || !hasRole || !hasFeature) {
          return false;
        }

        // If the item has children, only show it if at least one child is visible
        if (item.items && item.items.length > 0) {
          return item.items.length > 0; // Only show if there are visible children after filtering
        }

        // Item has permission and no children, or has visible children
        return true;
      });
  }

  private countsLoadedAt = 0;

  onMouseEnterItem(event: MenuItemDto | null) {
    if (!this.currentActiveItem() && event?.key == this.searchLoadsKey) {
      const searchLoadsItem = this.menuItems().find((m) => m.key == this.searchLoadsKey);

      if (!searchLoadsItem?.items) return;
      // Each pass of the mouse over the group asked again (two calls per hover, F-017); a few seconds old is fresh enough.
      if (Date.now() - this.countsLoadedAt < MENU_COUNTS_FRESH_MS) {
        this.currentActiveItem.set(event);
        return;
      }
      this.countsLoadedAt = Date.now();

      // Status views count from the load summary; exception views count from the Exceptions pill itself, because they
      // open that pill (option A, Dan 2026-10-16): the number in the menu is the number of rows the click shows.
      Promise.allSettled([
        lastValueFrom(this.loadService.getLoadSummary()),
        lastValueFrom(this.loadService.getWorkflowViewCounts({ workflowKey: 'exceptions', reasonsOnly: true } as LoadQueryFilterRequest))
      ]).then(([summaryResult, countsResult]) => {
        const items = searchLoadsItem.items!;
        const setCount = (prefix: string, count: number | undefined) => {
          const item = items.find((i) => i.label.startsWith(prefix));
          if (item) item.label = `${prefix} (${count ?? 0})`;
        };
        if (summaryResult.status === 'fulfilled') {
          const { intransit, dispatched, booked, awardedNotCovered } = summaryResult.value;
          setCount('In Transit', intransit);
          setCount('Dispatched', dispatched);
          setCount('Booked', booked);
          setCount('Awarded Not Covered', awardedNotCovered);
        }
        if (countsResult.status === 'fulfilled') {
          const kinds = countsResult.value.find((c) => c.key === 'exceptions')?.kinds ?? [];
          const kindCount = (key: string) => kinds.find((k) => k.key === key)?.count;
          setCount('Audit Exceptions', kindCount('audit'));
          setCount('Service Exceptions', kindCount('service'));
          setCount('Integration Exceptions', kindCount('integration'));
        }
        this.menuItems.set([...this.menuItems()]);
      });
    }
    this.currentActiveItem.set(event);
  }
}
